/**
 * pendingReschedule.js — PROD-921: an appointment row in 'pending_reschedule' reads as a PENDING
 * REQUEST in the patient app.
 *
 * orella-api update_appointment (the "NOTIFICATION + APPROVAL GATE" block) writes
 * status 'pending_reschedule' on the appointments row when a patient reschedules a confirmed
 * visit and the physician's case_acceptance_mode is 'manual_review'. The new time is not
 * confirmed until the physician approves it. Before this file the patient app had no reference to
 * that status, so each screen fell through to its "confirmed" branch (Visits card in the confirmed
 * colour, Deliver "Confirmed" pill, Case Home "Updated" / confirmed follow-up line, "Scheduled"
 * case badge).
 *
 * The mapping reuses the pending request handling and its existing copy. No new strings.
 * encounterConfig.js keeps an import-free mirror (STATUS_ALIASES); check-pending-reschedule PR-0
 * holds the two equal.
 *
 * Import-free on purpose: the gate executes it.
 */

export const PENDING_RESCHEDULE = 'pending_reschedule'

/** The status the patient app renders: pending_reschedule -> 'pending', anything else unchanged. */
export function asPendingRequest(status) {
  return status === PENDING_RESCHEDULE ? 'pending' : status
}

/** True when any appointment row the case carries waits on the physician for a reschedule. */
export function hasPendingReschedule(caseItem) {
  if (!caseItem || typeof caseItem !== 'object') return false
  const rows = [
    ...(Array.isArray(caseItem.appointments) ? caseItem.appointments : []),
    caseItem.appointment,
    caseItem.followUpAppointment,
  ]
  return rows.some((a) => !!a && a.status === PENDING_RESCHEDULE)
}

// ── PROD-1007 (Alex Q61) / PROD-1008 one-row shape: the patient keeps the OLD visit while a change is held ──
//
// A held reschedule is ONE appointments row (orella-api PROD-1008): status 'pending_reschedule' with the
// NEW date / time / type. The OLD slot lives only in the row's status_history: the LAST entry
// { status: 'pending_reschedule', heldDate, heldTime, heldDuration, heldType, heldStatus }, valid only
// while the row itself is pending_reschedule and that last entry says pending_reschedule too.
// Check-in eligibility and the check-in slot of a held case use that kept time, never the NEW one.
// Without a valid held entry (a row held before PROD-1008) there is nothing to check in to.
// No new copy.

const isFollowUpRow = (a) => {
  const category = String(a?.appointment_category ?? a?.appointmentCategory ?? '').toLowerCase()
  const type = String(a?.type ?? '').toLowerCase()
  return category === 'follow_up' || category === 'reopened' || type.includes('follow')
    || a?.is_followup === true || String(a?.id ?? '').startsWith('followup-')
}

/**
 * The kept (old) visit of a held row: { date, time, type } from the LAST status_history entry, or null
 * when the row is not pending_reschedule, has no history, the last entry is not pending_reschedule, or
 * heldDate / heldTime is missing.
 */
export function heldVisitSlot(row) {
  if (!row || typeof row !== 'object' || row.status !== PENDING_RESCHEDULE) return null
  let raw = row.status_history ?? row.statusHistory
  // P13 review F1: a history that arrives as a JSON string is parsed; an unparsable string is no history
  if (typeof raw === 'string') { try { raw = JSON.parse(raw) } catch { raw = null } }
  const history = Array.isArray(raw) ? raw : null
  if (!history || history.length === 0) return null
  const last = history[history.length - 1]
  if (!last || typeof last !== 'object' || last.status !== PENDING_RESCHEDULE) return null
  if (!last.heldDate || !last.heldTime) return null
  return { date: last.heldDate, time: last.heldTime, type: last.heldType || null }
}

/**
 * The slot a held case may still check in to: the kept (old) { date, time, type } of its held
 * initial-visit row, or null when the case is not held or the row carries no valid held entry.
 * A held follow-up row is skipped (as before). Accepts the case (appointments[] + appointment).
 */
export function confirmedCheckInSlot(caseItem) {
  if (!hasPendingReschedule(caseItem)) return null
  const rows = [...(Array.isArray(caseItem.appointments) ? caseItem.appointments : []), caseItem.appointment]
  for (const row of rows) {
    if (!row || isFollowUpRow(row) || row.source === 'manual_external') continue // PROD-1160: the outside visit is no Orella visit
    const slot = heldVisitSlot(row)
    if (slot) return slot
  }
  return null
}

/**
 * P20 (Q28, follow-up of PROD-1020 / PROD-1008): what Case Home shows while a reschedule is pending.
 * { booked: { date, time, type }, requested: { date, time } } for a pending_reschedule row that carries a
 * valid held entry: booked = the kept (old) slot from status_history[-1] (heldVisitSlot), requested = the
 * row's OWN date / time (the NEW time the physician has not confirmed). null for every other status, a row
 * without a valid held entry, or a row without its own date / time.
 */
export function requestedChangeOf(row) {
  const booked = heldVisitSlot(row)
  if (!booked || !row.date || !row.time) return null
  return { booked, requested: { date: row.date, time: row.time } }
}
